import React, { useState } from 'react';
import FileSaver from 'file-saver';
import autoTable from 'jspdf-autotable';
import axios from 'axios';
import { Workbook  } from 'exceljs';
import { jsPDF } from 'jspdf';

import { StateLanguage } from '../../languages/config/StateLanguage';
import { sanitizeFilename } from '../../scripts/document.filename.script';
import { Company } from '../../models/company.model';
import { GlobalSwal } from '../../styles/swal.style';
import { Quote } from '../../models/quote.model';
import { QuoteGroup } from '../../models/quote.group.model';
import { modalHide } from '../../utils/modal.util';
import { legendInvalid, legendValidInvalidRestart } from '../../utils/legend.data.util';
import { dateFormat } from '../../scripts/date.time.format.script';
import { accumulatedAmount } from '../../scripts/financial.calculation.script';
import { nameGroup } from '../../libraries/group.name.library';
import { formatContact } from '../../scripts/format.value.script';
import { normalizeStatus, statusDocumentValue } from '../../libraries/status.document.library';
import { QuoteDA } from '../../services/admin/quote.service';

import AppInputExportType from '../element/InputExportType';
import logo from '../../assets/themes/svg/logo-official.png';

export interface AppModalQuoteExportProps {
  companyForUser: Company | null,
  grouped: {state: boolean, desc: string},
  quotes: Quote[] | null,
  quoteGroups: QuoteGroup[] | null,
  filter?: {interval: string, start_date: string, end_date: string, contact: string, status: string, expiration: string, sort_field: string, sort_mode: string, group: string, search: string},
  vatSubject?: boolean
};

const AppModalQuoteExport: React.FunctionComponent<AppModalQuoteExportProps> = ({companyForUser, grouped, quotes, quoteGroups, filter, vatSubject}) => {
  const {lang} = StateLanguage()

  const [loadIndicator, setLoadIndicator] = useState('off')
  const [formatExport, setFormatExport] = useState({value: '', valid: false})

  const fileExcel = async (company: Company, tableTitle: string[][], tableData: any[][]) => {
    let workbook = new Workbook()
    let worksheet = workbook.addWorksheet('Quotes Data')
    const lastCol = vatSubject ? 'J' : 'I'

    worksheet.addRow([])
    worksheet.mergeCells(`A1:${lastCol}1`)
    worksheet.getRow(1).height = 35

    let imageBuffer = await axios.get(logo, {responseType: 'arraybuffer'})

    let imageLogotipo = workbook.addImage({
      buffer: imageBuffer.data,
      extension: 'png'
    })

    worksheet.addImage(imageLogotipo, { tl: {col: 0.1, row: 0.2}, ext: {width: 100, height: 30}})

    let titleRow = worksheet.addRow([`${company.name} - ${lang.labels.companyListOfQuotes}`])
    titleRow.font = {size: 18, bold: true}
    titleRow.getCell(1).alignment = {vertical:'distributed', horizontal: 'center'}

    worksheet.mergeCells(`A${titleRow.number}:${lastCol}${titleRow.number}`)
    worksheet.getRow(2).height = 30

    worksheet.addRow([])
    worksheet.mergeCells(`A3:${lastCol}3`)
    worksheet.getRow(3).height = 10

    let headerRow = worksheet.addRow(tableTitle[0])
    headerRow.font = {bold: true, color: {argb: 'FFFFFF'}}
    headerRow.eachCell( (cell) => {
      cell.alignment = {vertical:'distributed', horizontal: 'left'}
      cell.fill = {type: 'pattern', pattern: 'solid', fgColor: {argb: '2980BA'}, bgColor: {argb: '2980BA'}}
    })

    worksheet.getRow(4).height = 20

    tableData.forEach((item) => {
      let bodyRow = worksheet.addRow(item)

      if (grouped.state) {
        if (item.length === 1) {
          bodyRow.font = {bold: true}
        }
      }
    })

    if (grouped.state) {
      for (let i = 0; i < tableData.length; i++) {
        if (tableData[i].length === 1) {
          worksheet.mergeCells(`A${i + 5}:${lastCol}${i + 5}`)
        }
      }
    }

    worksheet.getColumn(1).width = 18;
    worksheet.getColumn(2).width = 30;
    worksheet.getColumn(3).width = 25;
    worksheet.getColumn(4).width = 13;
    worksheet.getColumn(5).width = 13;
    worksheet.getColumn(6).width = 13;
    worksheet.getColumn(7).width = 15;
    worksheet.getColumn(8).width = 15;
    if (vatSubject) { worksheet.getColumn(9).width = 15; }
    worksheet.getColumn(vatSubject ? 10 : 9).width = 17;

    workbook.xlsx.writeBuffer().then( data => {
      let blob = new Blob([data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      FileSaver.saveAs(blob, `Quotes_of_${company.name.replaceAll(' ', '_')}.xlsx`);
    })
  }

  const filePdf = async (company: Company, tableTitle: string[][], tableData: any[][]) => {
    let document = new jsPDF('landscape', 'mm', [340, 220])

    const rightAlignIndices = vatSubject ? [6, 7, 8, 9] : [6, 7, 8]

    if (grouped.state) {
      for (let i = 0; i < tableData.length; i++) {
        if (tableData[i].length === 1) {
          tableData[i][0] = {colSpan: vatSubject ? 10 : 9, content: tableData[i][0], styles: {fontStyle: 'bold'}}
        } else {
          for (const idx of rightAlignIndices) {
            tableData[i][idx] = {content: tableData[i][idx], styles: {halign: 'right'}}
          }
        }
      }
    } else {
      for (let i = 0; i < tableData.length; i++) {
        for (const idx of rightAlignIndices) {
          tableData[i][idx] = {content: tableData[i][idx], styles: {halign: 'right'}}
        }
      }
    }

    document.addImage(logo, 'PNG', 290, 5, 30, 9)

    document.setFontSize(18)
    document.setFont('helvetica', 'bold')
    document.text(`${company.name} - ${lang.labels.companyListOfQuotes}`, 25, 20)
    autoTable(document, {head: tableTitle, body: tableData, startY: 25})

    document.save(`Quotes_of_${sanitizeFilename(company.name).replaceAll(' ', '_')}.pdf`)
  }

  const buildTableData = (items: Quote[]) => {
    const tableData: any[][] = []
    for (let quote of items) {
      tableData.push([quote.quote_number, quote.concerning, formatContact(quote.customer), statusDocumentValue(lang,quote.status), dateFormat(lang.code, quote.valid_until), `${quote.currency.name} (${quote.currency.symbol})`, (Number(quote.amount.sub_total.toFixed(2))), Number(quote.amount.rebate_total.toFixed(2)), ...(vatSubject ? [Number(quote.amount.tax_total.toFixed(2))] : []), Number(quote.amount.total.toFixed(2))])
    }
    tableData.push(['', '', '', '', '', '', Number(accumulatedAmount(items).subtotal.toFixed(2)), Number(accumulatedAmount(items).rebate.toFixed(2)), ...(vatSubject ? [Number(accumulatedAmount(items).tax.toFixed(2))] : []), Number(accumulatedAmount(items).total.toFixed(2))])
    return tableData
  }

  const executeQuoteExport = async () => {
    setLoadIndicator('on')

    if (companyForUser && quotes && quoteGroups && formatExport.valid) {
      let tableTitle: string[][] = [[lang.labels.quoteNumber.toUpperCase(), lang.labels.concerning.toUpperCase(), lang.labels.customer.toUpperCase(), lang.labels.status.toUpperCase(), lang.labels.expiration.toUpperCase(), lang.labels.currency.toUpperCase(), lang.labels.subTotal.toUpperCase(), lang.labels.rebateTotal.toUpperCase(), ...(vatSubject ? [lang.labels.taxTotal.toUpperCase()] : []), lang.labels.amountTotal.toUpperCase()]]
      let tableData: any[][] = []

      if (grouped.state) {
        for (let i = 0; i < quoteGroups.length; i++) {
          tableData.push([`GROUP ${i + 1}: ${nameGroup(lang, grouped.desc, quoteGroups[i].group_detail).toUpperCase()}`])
          for (let quote of quoteGroups[i].quotes) {
            tableData.push([quote.quote_number, quote.concerning, formatContact(quote.customer), statusDocumentValue(lang,quote.status), dateFormat(lang.code, quote.valid_until), `${quote.currency.name} (${quote.currency.symbol})`, (Number(quote.amount.sub_total.toFixed(2))), Number(quote.amount.rebate_total.toFixed(2)), ...(vatSubject ? [Number(quote.amount.tax_total.toFixed(2))] : []), Number(quote.amount.total.toFixed(2))])
          }
          tableData.push(['', '', '', '', '', '', Number(accumulatedAmount(quoteGroups[i].quotes).subtotal.toFixed(2)), Number(accumulatedAmount(quoteGroups[i].quotes).rebate.toFixed(2)), ...(vatSubject ? [Number(accumulatedAmount(quoteGroups[i].quotes).tax.toFixed(2))] : []), Number(accumulatedAmount(quoteGroups[i].quotes).total.toFixed(2))])
        }
      } else if (filter) {
        const response = await QuoteDA.getQuotes(companyForUser.id, filter.interval, filter.start_date, filter.end_date, filter.contact, filter.status, filter.expiration, filter.sort_field, filter.sort_mode, '', 1, 20, filter.search, true)
        if (response.status === 200) {
          const allItems = (response.data.items ?? response.data.quotes ?? []).map((item: any) => ({ ...item, status: normalizeStatus(item.status) }))
          tableData = buildTableData(allItems)
        } else {
          tableData = buildTableData(quotes)
        }
      } else {
        tableData = buildTableData(quotes)
      }

      setTimeout( () => {
        switch (formatExport.value) {
          case 'excel':
            fileExcel(companyForUser, tableTitle, tableData)
            break;
          case  'pdf':
            filePdf(companyForUser, tableTitle, tableData)
            break;
        }

        setLoadIndicator('off')
        executeHideModalQuoteExport()
      }, 1000)
    } else {
      GlobalSwal.fire({
        text: lang.labels.sorryLooksLikeThereAreSomeErrorsTrySolve,
        icon: 'error',
        showConfirmButton: false,
        timer: 2200
      }).then( () => {
        legendInvalid('modal-quote-export-legend-format-export-type-required')
        setLoadIndicator('off')
      })
    }
  }

  const executeHideModalQuoteExport = () => {
    modalHide('modal-quote-export')

    setTimeout( () => {
      restartModal()
    }, 200 )
  }

  function restartModal() {
    legendValidInvalidRestart('modal-quote-export-legend-format-export-type-required')
    setFormatExport({value: '', valid: false})
  }

  return (
    <div id="modal-quote-export" className="modal fade" tabIndex={-1} aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
      <div className="modal-dialog modal-md">
        <div className="modal-content">
          <div className="modal-header">
            <h2 className="text-capitalize">{lang.labels.exportQuotes}</h2>
          </div>
          <div className="modal-body">
            <div className="form">
              <div className="fv-row">
                <label className="form-label required">{lang.labels.selectExportFormat}:</label>
                <AppInputExportType component={"modal-quote-export"} formatExport={formatExport} setFormatExport={setFormatExport}></AppInputExportType>
              </div>
            </div>
          </div>
          <div className="modal-footer flex-center">
            <button className="btn btn-secondary mx-2" type="reset" onClick={executeHideModalQuoteExport}>{lang.labels.discard}</button>
            <button className="btn btn-primary mx-2" type="button" data-kt-indicator={loadIndicator} onClick={executeQuoteExport}>
              <span className="indicator-label">{lang.labels.export}</span>
              <span className="indicator-progress">
                {lang.labels.pleaseWait}
                <span className="spinner-border spinner-border-sm align-middle ms-2"></span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AppModalQuoteExport;
